import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { MinusCircle, Plus } from "@phosphor-icons/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Input, Select, Textarea } from "@/components/Input";
import { usePageMeta } from "@/hooks/usePageMeta";
import { selectLanguage } from "@/i18n";
import { api, type ApiError, type EventAdmin } from "@/lib/api";
import {
  isoToLocalDate,
  isoToLocalTime,
  localDateTimeToIso,
  parseDeepLink,
} from "@/lib/deeplink";
import { recentEventsStorage } from "@/lib/storage";
import styles from "./NewEvent.module.css";

type EventCreatePayload = {
  name: string;
  eventAt: string;
  timezone: string;
  description?: string;
  location?: string;
  deadline?: string;
  creatorName?: string;
  creatorEmail?: string;
  expandable: boolean;
  anonymous: boolean;
  lang: string;
  initialItems: { name: string }[];
};

type FastApiValidationItem = {
  loc?: readonly (string | number)[];
  msg?: string;
  type?: string;
};

/** FastAPI 422 → { feldname: meldung } */
function mapFieldErrors(detail: unknown): Record<string, string> {
  if (!Array.isArray(detail)) return {};
  const map: Record<string, string> = {};
  for (const item of detail as FastApiValidationItem[]) {
    const loc = item.loc;
    if (!loc || loc.length < 2) continue;
    const key = loc[loc.length - 1];
    if (typeof key === "string" && !map[key]) {
      map[key] = item.msg ?? "Invalid value";
    }
  }
  return map;
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "Europe/Berlin";
  }
}

// Häufige TZs oben, dann alphabetisch. Admin kann „Andere…" nicht wählen
// im Select — unbekannte TZ kommt nur über Deep-Link-Parameter rein und
// wird dann als eigene Option angezeigt.
const COMMON_TIMEZONES = [
  "Europe/Berlin",
  "Europe/Vienna",
  "Europe/Zurich",
  "Europe/Amsterdam",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Warsaw",
  "Europe/London",
  "UTC",
];

export function NewEvent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  usePageMeta({
    title: t("pageMeta.newEventTitle"),
    description: t("pageMeta.newEventDescription"),
  });
  const prefill = useMemo(() => parseDeepLink(searchParams.toString()), [searchParams]);

  const [name, setName] = useState(prefill.title ?? "");
  const [eventDate, setEventDate] = useState(isoToLocalDate(prefill.date));
  const [eventTime, setEventTime] = useState(isoToLocalTime(prefill.date) || "19:00");
  const [timezone, setTimezone] = useState(browserTimezone());
  const [location, setLocation] = useState(prefill.location ?? "");
  const [description, setDescription] = useState("");
  const [deadlineDate, setDeadlineDate] = useState(isoToLocalDate(prefill.deadline));
  const [deadlineTime, setDeadlineTime] = useState(isoToLocalTime(prefill.deadline));
  const [creatorName, setCreatorName] = useState("");
  const [creatorEmail, setCreatorEmail] = useState("");
  const [items, setItems] = useState<string[]>(prefill.items.length ? prefill.items : [""]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (prefill.lang && prefill.lang !== i18n.language) {
      void selectLanguage(prefill.lang);
    }
  }, [prefill.lang, i18n.language]);

  // Stelle sicher, dass die Prefill-TZ im Select auftaucht — nur relevant, wenn
  // ein Deep-Link eine exotische IANA-Zone mitgibt.
  const availableTimezones = useMemo(() => {
    const set = new Set(COMMON_TIMEZONES);
    set.add(timezone);
    return [...set];
  }, [timezone]);

  const itemRefs = useRef<(HTMLInputElement | null)[]>([]);

  const updateItem = (idx: number, value: string) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? value : it)));

  const removeItem = (idx: number) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const addItem = () => {
    setItems((prev) => {
      const next = [...prev, ""];
      // Fokus setzen nach dem Render-Commit.
      window.setTimeout(() => {
        const el = itemRefs.current[next.length - 1];
        if (el) el.focus();
      }, 0);
      return next;
    });
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const trimmedItems = items
      .map((it) => it.trim())
      .filter((it) => it.length > 0);

    const payload: EventCreatePayload = {
      name: name.trim(),
      eventAt: localDateTimeToIso(eventDate, eventTime, timezone),
      timezone,
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      deadline:
        deadlineDate && deadlineTime
          ? localDateTimeToIso(deadlineDate, deadlineTime, timezone)
          : undefined,
      creatorName: creatorName.trim() || undefined,
      creatorEmail: creatorEmail.trim() || undefined,
      expandable: true,
      anonymous: false,
      lang: i18n.language.startsWith("en") ? "en" : "de",
      initialItems: trimmedItems.map((n) => ({ name: n })),
    };

    setSubmitting(true);
    try {
      const created = await api.post<EventAdmin>("/api/v1/events", payload);
      recentEventsStorage.upsert({
        shortcut: created.shortcut,
        key: created.adminKey,
        role: "admin",
        name: created.name,
        eventAt: created.eventAt,
      });
      navigate(`/proadmin/${created.shortcut}/${created.adminKey}`, { replace: true });
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 422) {
        const fieldMap = mapFieldErrors(e.detail);
        if (Object.keys(fieldMap).length > 0) {
          setFieldErrors(fieldMap);
          setFormError(null);
          return;
        }
      }
      const parts = [
        e.status ? `HTTP ${e.status}` : null,
        e.code ? `[${e.code}]` : null,
        e.message || t("newEvent.errorGeneric"),
      ].filter(Boolean);
      setFormError(parts.join(" "));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section>
      <h1 className={styles.heading}>{t("newEvent.heading")}</h1>
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <Input
          label={t("newEvent.nameLabel")}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("newEvent.namePlaceholder")}
          autoComplete="off"
          autoFocus
          error={fieldErrors.name}
          maxLength={100}
        />

        <DateTimePair
          dateLabel={t("newEvent.eventDateLabel")}
          timeLabel={t("newEvent.eventTimeLabel")}
          quickPickHint={t("newEvent.quickTimeHint")}
          date={eventDate}
          time={eventTime}
          onDate={setEventDate}
          onTime={setEventTime}
          required
          dateError={fieldErrors.eventAt}
        />

        <Select
          label={t("newEvent.timezoneLabel")}
          required
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
        >
          {availableTimezones.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </Select>

        <Input
          label={t("newEvent.locationLabel")}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={t("newEvent.locationPlaceholder")}
          autoComplete="off"
          maxLength={100}
        />

        <Textarea
          label={t("newEvent.descriptionLabel")}
          hint={t("newEvent.descriptionHint")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={2000}
        />

        <DateTimePair
          dateLabel={t("newEvent.deadlineLabel")}
          timeLabel={t("newEvent.eventTimeLabel")}
          quickPickHint={t("newEvent.quickTimeHint")}
          date={deadlineDate}
          time={deadlineTime}
          onDate={setDeadlineDate}
          onTime={setDeadlineTime}
          hint={t("newEvent.deadlineHint")}
          dateError={fieldErrors.deadline}
        />

        <div className={styles.row}>
          <Input
            label={t("newEvent.creatorNameLabel")}
            value={creatorName}
            onChange={(e) => setCreatorName(e.target.value)}
            autoComplete="name"
            maxLength={50}
          />
          <Input
            label={t("newEvent.creatorEmailLabel")}
            type="email"
            value={creatorEmail}
            onChange={(e) => setCreatorEmail(e.target.value)}
            hint={t("newEvent.creatorEmailHint")}
            autoComplete="email"
            maxLength={254}
          />
        </div>

        <fieldset className={styles.fieldset}>
          <legend>{t("newEvent.itemsLabel")}</legend>
          <div className={styles.itemsList}>
            {items.map((value, idx) => (
              <div className={styles.itemRow} key={idx}>
                <div className={styles.itemInputWrap}>
                  <Input
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    label=""
                    value={value}
                    onChange={(e) => updateItem(idx, e.target.value)}
                    placeholder={t("newEvent.itemPlaceholder")}
                    maxLength={100}
                  />
                </div>
                {items.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="compact"
                    onClick={() => removeItem(idx)}
                    aria-label={t("newEvent.removeItem")}
                  >
                    <MinusCircle size={20} weight="regular" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div className={styles.itemsFooter}>
            <Button type="button" variant="ghost" size="compact" onClick={addItem}>
              <Plus size={16} weight="regular" />
              {t("newEvent.addItem")}
            </Button>
            <span className={styles.itemsHint}>{t("newEvent.itemsHint")}</span>
          </div>
        </fieldset>

        {formError && <div className={styles.errorBox}>{formError}</div>}

        <div className={styles.submit}>
          <Button type="submit" disabled={submitting}>
            {submitting ? t("newEvent.submitting") : t("newEvent.submit")}
          </Button>
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// DateTimePair — Datum + Uhrzeit getrennt. Uhrzeit rastert auf 30-Min-Schritte
// (step="1800"), Quick-Pick-Chips für die häufigsten Startzeiten.
// ---------------------------------------------------------------------------

const QUICK_TIMES = ["14:00", "17:00", "18:00", "19:00", "19:30", "20:00"];

function DateTimePair({
  dateLabel,
  timeLabel,
  quickPickHint,
  date,
  time,
  onDate,
  onTime,
  required,
  hint,
  dateError,
}: {
  dateLabel: string;
  timeLabel: string;
  quickPickHint: string;
  date: string;
  time: string;
  onDate: (v: string) => void;
  onTime: (v: string) => void;
  required?: boolean;
  hint?: string;
  dateError?: string | null;
}) {
  return (
    <div className={styles.dateTimePair}>
      <div className={styles.dateTimeGrid}>
        <Input
          label={dateLabel}
          required={required}
          type="date"
          value={date}
          onChange={(e) => onDate(e.target.value)}
          error={dateError}
        />
        <Input
          label={timeLabel}
          required={required}
          type="time"
          step={1800}
          value={time}
          onChange={(e) => onTime(e.target.value)}
        />
      </div>
      <div className={styles.quickPickRow}>
        <span className={styles.quickPickHint}>{quickPickHint}</span>
        {QUICK_TIMES.map((val) => {
          const active = time === val;
          return (
            <button
              key={val}
              type="button"
              onClick={() => onTime(val)}
              className={`${styles.quickPickChip} ${active ? styles.quickPickChipActive : ""}`}
              aria-pressed={active}
            >
              {val}
            </button>
          );
        })}
      </div>
      {hint && <p className={styles.dateTimeHint}>{hint}</p>}
    </div>
  );
}
